// The opening choice: what kind of watching is this run for?
//
// The trade this offers is SPEED against DETAIL, and nothing else.
//
// An earlier version of this file claimed the choice also fixed the predator:prey
// ratio, on the theory that compressed lifespans inflate prey production and so
// inflate predator numbers. That was measured and is WRONG: stretching life
// history made the ratio worse (1:12 -> 1:7), because longer-lived wolves
// accumulate just as prey production falls, and our wolves are breeding-capped
// rather than food-capped. The ratio turns out to be set by TERRITORY SPACING
// (see breedingGroupRadius in species.ts), which is a separate dial and free.
//
// So this screen makes one honest offer: fast generations at coarse behavioural
// detail, or life-like behaviour with slow generations.
//
// Self-contained like guide.ts: builds its own overlay and scoped styles, and
// resolves a choice. main.ts keeps the sim paused until it does.

const STYLE_ID = "ecosim-start-css";

/**
 * Life-history stretch for the "watch animals" mode.
 *
 * PROVISIONAL. It controls how long an animal lives relative to how fast it
 * moves, so at 6 a generation takes six times longer to watch and every
 * individual life is six times more of it. It does NOT affect the predator:prey
 * ratio — that is territory spacing. Kept modest because the measured cost is
 * linear and the benefit (watching one animal's whole life) saturates quickly.
 */
export const BEHAVIOUR_LIFE_SCALE = 6;

export interface StartChoice {
  lifeScale: number;
  speedMult: number;
}

const CSS = `
.start-back {
  position: fixed; inset: 0; z-index: 200;
  background: rgba(6, 9, 6, 0.9);
  backdrop-filter: blur(4px);
  display: flex; align-items: center; justify-content: center;
  padding: var(--s-4, 16px);
}
.start-back[hidden] { display: none; }

.start {
  width: min(880px, 100%);
  max-height: 92dvh; overflow-y: auto;
  background: var(--panel, #161a16);
  border: 1px solid var(--line, #333a31);
  border-radius: var(--r-lg, 14px);
  box-shadow: 0 30px 90px rgba(0,0,0,.7);
  color: var(--ink, #e8e4d9);
  padding: 26px 28px 24px;
}
.start h1 {
  margin: 0 0 3px;
  font-family: var(--font-label, system-ui, sans-serif);
  font-size: 22px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .18em;
}
.start .sub {
  margin: 0 0 20px; color: var(--muted, #8c8f84);
  font-family: var(--font-prose, Georgia, serif); font-style: italic; font-size: 14px;
}

.start-cards { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
@media (max-width: 720px) { .start-cards { grid-template-columns: 1fr; } }

.start-card {
  text-align: left;
  background: var(--panel-2, #1e231d);
  border: 1px solid var(--line, #333a31);
  border-radius: var(--r-md, 10px);
  padding: 16px 16px 14px;
  cursor: pointer;
  color: inherit;
  font: inherit;
  transition: border-color .12s ease, background .12s ease;
}
.start-card:hover { border-color: var(--muted, #8c8f84); }
.start-card[aria-pressed="true"] {
  border-color: var(--deer, #d8ab6a);
  background: #232a20;
}
.start-card h2 {
  margin: 0 0 2px;
  font-family: var(--font-label, system-ui, sans-serif);
  font-size: 16px; font-weight: 600;
  text-transform: uppercase; letter-spacing: .14em;
  color: var(--deer, #d8ab6a);
}
.start-card .lede {
  margin: 0 0 10px; font-size: 13.5px; line-height: 1.5;
  font-family: var(--font-prose, Georgia, serif);
}
.start-card dl { margin: 0; display: grid; grid-template-columns: auto 1fr; gap: 3px 10px; font-size: 12.5px; }
.start-card dt {
  font-family: var(--font-label, system-ui, sans-serif);
  text-transform: uppercase; letter-spacing: .1em;
  color: var(--dim, #5f6357); white-space: nowrap;
}
.start-card dd { margin: 0; font-family: var(--font-data, monospace); }
.start-card .cost { color: var(--warn, #e0a458); }

.start-foot { display: flex; align-items: center; gap: 14px; margin-top: 18px; }
.start-go {
  font-family: var(--font-label, system-ui, sans-serif);
  font-size: 15px; text-transform: uppercase; letter-spacing: .16em;
  padding: 10px 26px; border-radius: 999px; cursor: pointer;
  border: 1px solid var(--deer, #d8ab6a);
  background: var(--deer, #d8ab6a); color: var(--bg, #0a0c0a);
}
.start-go:disabled { opacity: .35; cursor: default; }
.start-note {
  color: var(--dim, #5f6357); font-size: 12.5px;
  font-family: var(--font-prose, Georgia, serif); font-style: italic;
}
`;

interface Mode {
  key: string;
  title: string;
  lede: string;
  rows: [string, string, boolean][]; // label, value, isCost
  choice: StartChoice;
}

const MODES: Mode[] = [
  {
    key: "evolution",
    title: "Watch evolution",
    lede:
      "Fast-forward through hundreds of generations and watch traits drift. Senses, speed and body size visibly respond to whatever the world is doing to them — but each animal flickers past in seconds.",
    rows: [
      ["Generations", "~50 in minutes", false],
      ["Behaviour", "fully real, tick for tick", false],
      ["Individual lives", "a deer is born and dies in ~30 s", true],
    ],
    choice: { lifeScale: 1, speedMult: 100 },
  },
  {
    key: "behaviour",
    title: "Watch animals",
    lede:
      "Lives run long enough to follow one animal through a day — bedding down, grazing, being hunted. The ecology is identical; only the demographic clock slows.",
    rows: [
      ["Individual lives", "long enough to follow one animal", false],
      ["Behaviour", "fully real, tick for tick", false],
      ["Generations", "hours, not minutes", true],
    ],
    choice: { lifeScale: BEHAVIOUR_LIFE_SCALE, speedMult: 1 },
  },
];

/**
 * Show the opening choice and resolve once the user commits.
 * Never rejects; there is no way to dismiss without choosing.
 */
export function askStartChoice(): Promise<StartChoice> {
  if (!document.getElementById(STYLE_ID)) {
    const s = document.createElement("style");
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  const back = document.createElement("div");
  back.className = "start-back";
  back.innerHTML = `
    <div class="start" role="dialog" aria-modal="true" aria-label="Choose how to watch">
      <h1>EcoSim</h1>
      <p class="sub">an evolving ecosystem — watch generations, or watch animals</p>
      <div class="start-cards">
        ${MODES.map(
          (m, i) => `
          <button class="start-card" type="button" data-i="${i}" aria-pressed="${i === 0}">
            <h2>${m.title}</h2>
            <p class="lede">${m.lede}</p>
            <dl>${m.rows
              .map(
                ([k, v, cost]) =>
                  `<dt>${k}</dt><dd class="${cost ? "cost" : ""}">${v}</dd>`,
              )
              .join("")}</dl>
          </button>`,
        ).join("")}
      </div>
      <div class="start-foot">
        <button class="start-go" type="button">Start</button>
        <span class="start-note">You can change this later, but it restarts the world — animals already alive carry the lifespans they were born with.</span>
      </div>
    </div>`;

  document.body.appendChild(back);
  const cards = [...back.querySelectorAll<HTMLButtonElement>(".start-card")];
  const go = back.querySelector<HTMLButtonElement>(".start-go")!;
  let picked = 0;

  const select = (i: number): void => {
    picked = i;
    cards.forEach((c, j) => c.setAttribute("aria-pressed", String(i === j)));
  };
  cards.forEach((c, i) => c.addEventListener("click", () => select(i)));

  return new Promise<StartChoice>((resolve) => {
    go.addEventListener("click", () => {
      back.remove();
      resolve(MODES[picked].choice);
    });
    go.focus();
  });
}
